import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, ArrowRight, X, CheckCircle2, AlertCircle, Clock, Heart, AlertTriangle, Coffee, Loader2, UserPlus, Plus, Minus } from 'lucide-react';
import { useAuth } from '../context/AuthContext'; 
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added

const TripsPage = () => {
  const { user } = useAuth();
  const { isDark } = useTheme(); // 👈 Access Theme State
  
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Primary User Form State
  const [formData, setFormData] = useState({
    name: '', 
    email: '', 
    phone: '', 
    emergencyContactName: '', 
    emergencyContactRelation: '', 
    emergencyContactPhone: '', 
    dietary: 'Vegetarian', 
    medicalInfo: ''
  });

  // Co-Traveler States
  const [travelMode, setTravelMode] = useState('alone'); // 'alone' or 'group'
  const [guestCount, setGuestCount] = useState(1);
  const [guests, setGuests] = useState([{ name: '', phone: '', dietary: 'Vegetarian', medicalInfo: '' }]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.mobile_number || ''
      }));
    }
  }, [user]);

  // 👇 Forced all registrations to open (isOpen: true for all)
  const trips = [
    /*{
      id: 1, title: "Konkan Calling", location: "Maharashtra, India",
      desc: "A trip along the Konkan coastline, experiencing local culture, authentic seafood, and pristine untouched beaches.",
      date: "Last week of July 2026", seatsTotal: 20, seatsFilled: 8, isOpen: true,
      image: "https://framerusercontent.com/images/UrxKuVBiDryZ9wXdPFNIQKi1Pw.png" 
    },
    {
      id: 2, title: "Rajgad Monsoon Trek", location: "Pune District, India",
      desc: "A thrilling 2-day trek to Rajgad fort. Explore the historical capital from where Chhatrapati Shivaji Maharaj ruled for 26 years.",
      date: "July 2026 (2nd Weekend)", seatsTotal: 30, seatsFilled: 12, isOpen: true,
      image: "https://media.assettype.com/outlooktraveller/2024-06/6632351e-c1b0-45e5-98d0-1d378e2f16ba/shutterstock_2418844171.jpg?w=1200&ar=40%3A21&auto=format%2Ccompress&ogImage=true&mode=crop&enlarge=true&overlay=false&overlay_position=bottom&overlay_width=100" 
    },
    {
      id: 3, title: "Valley of Flowers Trek", location: "Uttarakhand, India",
      desc: "Witness the high-altitude Himalayan valley blooming with hundreds of species of alpine flowers.",
      date: "9th - 15th August 2026", seatsTotal: 25, seatsFilled: 10, isOpen: true,
      image: "https://himtrek.co.in/wp-content/uploads/2026/04/flowers-1200x700.webp" 
    },*/
    {
      id: 4, title: "Bali Escape", location: "Bali, Indonesia",
      desc: "Experience the Island of Gods. Temples, rice terraces, and stunning sunsets await.",
      date: "September 2026", seatsTotal: 20, seatsFilled: 5, isOpen: true,
      image: "https://visitbalitour.com/wp-content/uploads/2025/10/Bali-8-days-package.jpg" 
    },
    {
      id: 6, title: "Jibhi Tirthan Valley", location: "Himachal Pradesh, India",
      desc: "An autumn retreat in the peaceful, offbeat valleys of Himachal surrounded by pine forests.",
      date: "October 2026", seatsTotal: 20, seatsFilled: 4, isOpen: true,
      image: "https://static.toiimg.com/thumb/msid-93048186,width-748,height-499,resizemode=4,imgsize-211710/.jpg"
    },
    {
      id: 5, title: "Sikkim & Darjeeling", location: "Northeast India",
      desc: "Explore winter landscapes, ancient monasteries, and world-famous rolling tea gardens.",
      date: "November 2026", seatsTotal: 25, seatsFilled: 6, isOpen: true,
      image: "https://s7ap1.scene7.com/is/image/incredibleindia/2-chungthang-mangan-sikkim-2-city-hero?qlt=82&ts=1726655834488"
    }
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGuestCountChange = (delta) => {
    const newCount = guestCount + delta;
    if (newCount < 1 || newCount > 10) return; 
    setGuestCount(newCount);

    if (delta > 0) {
      setGuests([...guests, { name: '', phone: '', dietary: 'Vegetarian', medicalInfo: '' }]);
    } else {
      setGuests(guests.slice(0, newCount));
    }
  };

  const handleGuestChange = (index, field, value) => {
    const updatedGuests = [...guests];
    updatedGuests[index][field] = value;
    setGuests(updatedGuests);
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    if (!/^[6-9]\d{9}$/.test(formData.phone.trim())) {
      alert("Please enter a valid 10-digit primary phone number.");
      return;
    }

    if (!/^[6-9]\d{9}$/.test(formData.emergencyContactPhone.trim())) {
      alert("Please enter a valid emergency contact phone number.");
      return;
    }

    if (travelMode === "group") {
      for (let i = 0; i < guests.length; i++) {
        if (!/^[6-9]\d{9}$/.test(guests[i].phone.trim())) {
          alert(`Please enter a valid phone number for Guest ${i + 1}.`);
          return;
        }
      }
    }

    setIsSubmitting(true);

    const submissionData = {
      trip_name: selectedTrip.title,
      primary_user: {
        ...formData,
      },
      additional_guests: travelMode === "group" ? guests : [],
    };

    try {
      const response = await fetch("/api/send_trip_details", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Unable to submit registration.");
      }

      setSelectedTrip(null);
      setTravelMode("alone");
      setGuestCount(1);
      setGuests([
        {
          name: "",
          phone: "",
          dietary: "Vegetarian",
          medicalInfo: "",
        },
      ]);

      setToastMessage(
        data.message || `Successfully registered for ${selectedTrip.title}!`
      );

      setTimeout(() => setToastMessage(""), 4000);
    } catch (error) {
      console.error(error);
      alert(error.message || "Something went wrong while submitting your registration.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f9fafb', paddingBottom: '80px', fontFamily: 'system-ui, -apple-system, sans-serif', transition: 'background-color 0.3s', boxSizing: 'border-box' }}>
      
      <div style={{ backgroundColor: isDark ? '#030712' : '#111827', padding: '100px 16px 50px 16px', textAlign: 'center', position: 'relative', overflow: 'hidden', transition: 'background-color 0.3s' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.2 }} />
        <div style={{ position: 'relative', zIndex: 10, maxWidth: '800px', margin: '0 auto', boxSizing: 'border-box' }}>
          <h1 style={{ color: 'white', fontSize: 'clamp(32px, 5vw, 42px)', fontWeight: '800', marginBottom: '12px' }}>Odessey Group Trips</h1>
          <p style={{ color: '#9ca3af', fontSize: '15px', lineHeight: '1.6' }}>Join our curated group adventures. Handpicked destinations, expert guides, and a community of travellers just like you.</p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '30px auto 0', padding: '0 16px', position: 'relative', zIndex: 20, boxSizing: 'border-box' }}>
        
        <div style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#16a34a', boxShadow: '0 0 10px rgba(22,163,74,0.5)', animation: 'pulse 2s infinite' }} />
            <h2 style={{ fontSize: '22px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', margin: 0 }}>Actively Booking</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '25px' }}>
            {trips.map(trip => (
              <div key={trip.id} style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', overflow: 'hidden', boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.05)', border: isDark ? '1px solid #374151' : '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s, background-color 0.3s, border-color 0.3s', boxSizing: 'border-box' }} onMouseEnter={(e)=>e.currentTarget.style.transform='translateY(-5px)'} onMouseLeave={(e)=>e.currentTarget.style.transform='translateY(0)'}>
                <div style={{ height: '200px', backgroundImage: `url(${trip.image})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '12px', right: '12px', backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)', padding: '5px 10px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CheckCircle2 size={13} /> Registration Open
                  </div>
                </div>
                <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: isDark ? '#38bdf8' : '#6366f1', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '8px' }}>
                    <MapPin size={13} /> {trip.location}
                  </div>
                  <h3 style={{ fontSize: '20px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', margin: '0 0 8px 0' }}>{trip.title}</h3>
                  <p style={{ color: isDark ? '#d1d5db' : '#6b7280', fontSize: '13px', lineHeight: '1.5', marginBottom: '16px', flex: 1 }}>{trip.desc}</p>
                  
                  <div style={{ backgroundColor: isDark ? '#111827' : '#f8fafc', borderRadius: '12px', padding: '12px', marginBottom: '16px', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: isDark ? '#d1d5db' : '#4b5563', fontSize: '13px', marginBottom: '8px', fontWeight: '500' }}>
                      <Calendar size={15} color="#0284c7" /> {trip.date}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: isDark ? '#d1d5db' : '#4b5563', fontSize: '13px', fontWeight: '500' }}>
                      <Users size={15} color="#ea580c" /> 
                      <span>{trip.seatsTotal - trip.seatsFilled} seats left <span style={{ color: '#9ca3af', fontWeight: 'normal' }}>(Total: {trip.seatsTotal})</span></span>
                    </div>
                  </div>

                  <button onClick={() => setSelectedTrip(trip)} style={{ width: '100%', padding: '12px', backgroundColor: isDark ? '#374151' : '#111827', color: 'white', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : 'none', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'background 0.2s', boxSizing: 'border-box' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#4b5563' : '#1f2937'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#374151' : '#111827'}>
                    Register Now <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* REGISTRATION MODAL */}
      {selectedTrip && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, backgroundColor: 'rgba(17, 24, 39, 0.8)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box' }}>
          <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', padding: '30px 20px', width: '100%', maxWidth: '550px', position: 'relative', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', margin: 'auto', maxHeight: '90vh', overflowY: 'auto', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box' }} className="hide-scrollbar">
            
            <button onClick={() => setSelectedTrip(null)} style={{ position: 'absolute', top: '15px', right: '15px', background: isDark ? '#374151' : '#f3f4f6', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: isDark ? 'white' : '#4b5563' }}>
              <X size={16} />
            </button>

            <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '4px' }}>Join the Journey</h2>
            <p style={{ color: '#16a34a', fontWeight: 'bold', fontSize: '14px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '5px' }}><MapPin size={15}/> {selectedTrip.title}</p>

            <form onSubmit={handleRegisterSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '5px' }}>Full Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Your Name" style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '5px' }}>Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="Your Number" style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827' }} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', marginBottom: '5px' }}>Email Address</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="Your Email" disabled style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', backgroundColor: isDark ? '#111827' : '#f3f4f6', color: isDark ? '#9ca3af' : '#6b7280', fontSize: '13px', cursor: 'not-allowed' }} />
              </div>

              {/* Emergency Contact Details */}
              <div style={{ backgroundColor: isDark ? '#431407' : '#fff7ed', padding: '14px', borderRadius: '12px', border: isDark ? '1px solid #7c2d12' : '1px solid #fed7aa', boxSizing: 'border-box' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 'bold', color: '#ea580c', marginBottom: '10px' }}>
                  <AlertTriangle size={13} color="#ea580c"/> Emergency Contact Details
                </label>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '10px' }}>
                  <input type="text" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleInputChange} required placeholder="Contact Name" style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid #fdba74', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} />
                  <input type="text" name="emergencyContactRelation" value={formData.emergencyContactRelation} onChange={handleInputChange} required placeholder="Relation (e.g. Parent)" style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid #fdba74', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} />
                </div>
                
                <input
                  type="tel"
                  name="emergencyContactPhone"
                  value={formData.emergencyContactPhone}
                  maxLength={10}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: '1px solid #fdba74', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emergencyContactPhone: e.target.value.replace(/\D/g, "")
                    })
                  }
                  required placeholder="Emergency Phone Number"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#111827', marginBottom: '6px' }}>
                    <Coffee size={13} color="#0284c7"/> Dietary Preference
                  </label>
                  <select name="dietary" value={formData.dietary} onChange={handleInputChange} style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }}>
                    <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="Vegetarian">Vegetarian</option>
                    <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="Non-Vegetarian">Non-Vegetarian</option>
                    <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="Jain">Jain Food</option>
                    <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="Vegan">Vegan</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#111827', marginBottom: '6px' }}>
                    <Heart size={13} color="#dc2626"/> Medical Info (Optional)
                  </label>
                  <input type="text" name="medicalInfo" value={formData.medicalInfo} onChange={handleInputChange} placeholder="E.g., Asthma, allergy" style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }} />
                </div>
              </div>

              <hr style={{ border: 'none', borderTop: isDark ? '1px solid #374151' : '1px solid #e5e7eb', margin: '4px 0' }} />

              {/* Travel Group Selection */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#111827', marginBottom: '6px' }}>Who is traveling?</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={() => setTravelMode('alone')} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: travelMode === 'alone' ? '2px solid #16a34a' : (isDark ? '1px solid #4b5563' : '1px solid #d1d5db'), backgroundColor: travelMode === 'alone' ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#111827' : 'white'), color: travelMode === 'alone' ? (isDark ? '#a7f3d0' : '#16a34a') : (isDark ? '#9ca3af' : '#4b5563'), fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s' }}>
                    Just Me
                  </button>
                  <button type="button" onClick={() => setTravelMode('group')} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: travelMode === 'group' ? '2px solid #16a34a' : (isDark ? '1px solid #4b5563' : '1px solid #d1d5db'), backgroundColor: travelMode === 'group' ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#111827' : 'white'), color: travelMode === 'group' ? (isDark ? '#a7f3d0' : '#16a34a') : (isDark ? '#9ca3af' : '#4b5563'), fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                    <UserPlus size={15} /> Adding Others
                  </button>
                </div>
              </div>

              {/* Dynamic Guest Generator UI */}
              {travelMode === 'group' && (
                <div style={{ backgroundColor: isDark ? '#111827' : '#f8fafc', padding: '16px', borderRadius: '14px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: '16px', animation: 'fadeIn 0.3s ease-out', boxSizing: 'border-box' }}>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#374151' }}>Number of additional guests</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: isDark ? '#1f2937' : 'white', padding: '4px', borderRadius: '8px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db' }}>
                      <button type="button" onClick={() => handleGuestCountChange(-1)} disabled={guestCount <= 1} style={{ background: 'none', border: 'none', cursor: guestCount <= 1 ? 'not-allowed' : 'pointer', color: guestCount <= 1 ? '#4b5563' : '#4b5563', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3px' }}><Minus size={15} /></button>
                      <span style={{ fontSize: '14px', fontWeight: 'bold', color: isDark ? 'white' : '#111827', width: '18px', textAlign: 'center' }}>{guestCount}</span>
                      <button type="button" onClick={() => handleGuestCountChange(1)} disabled={guestCount >= 10} style={{ background: 'none', border: 'none', cursor: guestCount >= 10 ? 'not-allowed' : 'pointer', color: guestCount >= 10 ? '#4b5563' : '#4b5563', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3px' }}><Plus size={15} /></button>
                    </div>
                  </div>

                  {guests.map((guest, idx) => (
                    <div key={idx} style={{ borderTop: idx !== 0 ? (isDark ? '1px solid #374151' : '1px solid #e5e7eb') : 'none', paddingTop: idx !== 0 ? '16px' : '0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <h4 style={{ fontSize: '12px', fontWeight: 'bold', color: '#16a34a', margin: 0, textTransform: 'uppercase' }}>Guest {idx + 1} Details</h4>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                        <div>
                          <input type="text" value={guest.name} onChange={(e) => handleGuestChange(idx, 'name', e.target.value)} required placeholder="Full Name" style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#1f2937' : 'white', color: isDark ? 'white' : '#111827' }} />
                        </div>
                        <div>
                          <input
                            type="tel"
                            value={guest.phone}
                            maxLength={10}
                            onChange={(e) =>
                              handleGuestChange(
                                idx,
                                "phone",
                                e.target.value.replace(/\D/g, "")
                              )
                            }
                            pattern="[6-9][0-9]{9}"
                            title="Enter a valid 10-digit mobile number"
                            required
                            placeholder="Phone Number"
                            style={{
                              width: "100%",
                              boxSizing: "border-box",
                              padding: "10px 12px",
                              borderRadius: "8px",
                              border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db',
                              outline: "none",
                              fontSize: "13px",
                              backgroundColor: isDark ? '#1f2937' : 'white',
                              color: isDark ? 'white' : '#111827'
                            }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                        <div>
                          <select value={guest.dietary} onChange={(e) => handleGuestChange(idx, 'dietary', e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#1f2937' : 'white', color: isDark ? 'white' : '#111827' }}>
                            <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="Vegetarian">Vegetarian</option>
                            <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="Non-Vegetarian">Non-Vegetarian</option>
                            <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="Jain">Jain Food</option>
                            <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="Vegan">Vegan</option>
                          </select>
                        </div>
                        <div>
                          <input type="text" value={guest.medicalInfo} onChange={(e) => handleGuestChange(idx, 'medicalInfo', e.target.value)} placeholder="Medical Info (Optional)" style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '8px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', outline: 'none', fontSize: '13px', backgroundColor: isDark ? '#1f2937' : 'white', color: isDark ? 'white' : '#111827' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button type="submit" disabled={isSubmitting} style={{ marginTop: '8px', backgroundColor: isDark ? '#374151' : '#111827', color: 'white', padding: '14px', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', border: isDark ? '1px solid #4b5563' : 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxSizing: 'border-box' }}>
                {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : 'Confirm Registration Request'}
              </button>
              <p style={{ textAlign: 'center', fontSize: '11px', color: isDark ? '#9ca3af' : '#6b7280', margin: 0 }}>No payment required yet. Our team will verify seat availability and send a payment link.</p>
            </form>
          </div>
        </div>
      )}

      {toastMessage && (
        <div style={{ position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#111827', color: 'white', padding: '12px 24px', borderRadius: '50px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', zIndex: 99999, animation: 'slideUp 0.3s ease-out forwards', boxSizing: 'border-box' }}>
          <CheckCircle2 size={18} color="#4ade80" /> {toastMessage}
        </div>
      )}

      <style>{`
        @keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(22,163,74, 0.4); } 70% { box-shadow: 0 0 0 10px rgba(22,163,74, 0); } 100% { box-shadow: 0 0 0 0 rgba(22,163,74, 0); } }
        @keyframes slideUp { from { opacity: 0; transform: translate(-50%, 30px); } to { opacity: 1; transform: translate(-50%, 0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default TripsPage;